import React, { useState } from 'react';
import { Copy, Check } from './icons';
import { getContrastRatio } from '../utils/colorAnalysis';
import type { RGB } from '../types';
import { rgbToHex, rgbToHsl, rgbToHsv, rgbToCmyk, rgbToOklch, getNearestColorName } from '../utils/colors';

interface ColorIdentityCardProps {
  color: RGB;
}

const ColorIdentityCard: React.FC<ColorIdentityCardProps> = ({ color }) => {
  const [copied, setCopied] = useState<string | null>(null);

  const hex = rgbToHex(color.r, color.g, color.b);
  const name = getNearestColorName(color.r, color.g, color.b);
  const hsl = rgbToHsl(color.r, color.g, color.b);
  const hsv = rgbToHsv(color.r, color.g, color.b);
  const cmyk = rgbToCmyk(color.r, color.g, color.b);
  const oklch = rgbToOklch(color.r, color.g, color.b);

  const useBlack = getContrastRatio(hex, '#000000') >= getContrastRatio(hex, '#FFFFFF');
  const textColor = useBlack ? 'text-black' : 'text-white';
  const subTextColor = useBlack ? 'text-black' : 'text-white';

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(null), 1500);
  };

  const values = [
    { label: 'HEX', value: hex },
    { label: 'RGB', value: `${color.r}, ${color.g}, ${color.b}` },
    { label: 'HSL', value: `${hsl.h}°, ${hsl.s}%, ${hsl.l}%` },
    { label: 'HSV', value: `${hsv.h}°, ${hsv.s}%, ${hsv.v}%` },
    { label: 'CMYK', value: `${cmyk.c}, ${cmyk.m}, ${cmyk.y}, ${cmyk.k}` },
    { label: 'OKLCH', value: `${oklch.l}% ${(oklch.c / 100).toFixed(3)} ${oklch.h}°` },
  ];

  return (
    <div className="ui-panel overflow-hidden">
      <div
        className="px-6 py-8 flex items-end justify-between transition-colors duration-300"
        style={{ backgroundColor: hex }}
      >
        <div>
          <h2 className={`text-2xl md:text-3xl font-bold ${textColor}`}>{name}</h2>
          <p className={`text-sm font-mono mt-1 ${subTextColor}`}>{hex}</p>
        </div>
        <button
          onClick={() => handleCopy(hex)}
          className={`p-2 rounded-lg transition-colors ${useBlack ? 'hover:bg-black/10' : 'hover:bg-white/20'}`}
          title="Copy hex"
        >
          {copied === hex
            ? <Check size={18} className={textColor} />
            : <Copy size={18} className={textColor} />}
        </button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-px bg-surface-border">
        {values.map((v) => (
          <button
            key={v.label}
            onClick={() => handleCopy(v.value)}
            className="bg-white p-3 hover:bg-brand-50 transition-colors text-left group"
          >
            <span className="text-xs font-bold uppercase tracking-wider text-ink-muted block">{v.label}</span>
            <span className="text-xs font-mono text-ink mt-0.5 block group-hover:text-brand-600 transition-colors">
              {v.value}
            </span>
            {copied === v.value && (
              <span className="text-xs text-emerald-600 font-medium">Copied!</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

export default ColorIdentityCard;
